import React, { useState, useEffect } from "react";
import { sellerService } from "../../services/enhancedCrudService";
import { toast } from "react-toastify";

const TileInquiryClickList = () => {
  const [inquiryClicks, setInquiryClicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchInquiryClicks = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        page_size: pagination.pageSize,
      };

      const response = await sellerService.getTileInquiryClickList(params);

      if (response && response.inquiry_clicks) {
        setInquiryClicks(response.inquiry_clicks);
        setPagination((prev) => ({
          ...prev,
          total: response.count || 0,
          totalPages: Math.ceil((response.count || 0) / pagination.pageSize),
        }));
      } else {
        setInquiryClicks([]);
      }
    } catch (error) {
      console.error("Error fetching tile inquiry clicks:", error);
      toast.error("Failed to load inquiry click list");
      setInquiryClicks([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    fetchInquiryClicks();
  }, [pagination.page, pagination.pageSize]);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h4 className="card-title">Tile Inquiry Click List</h4>
            <div className="card-actions">
              <span className="badge badge-info">
                Total: {pagination.total} clicks
              </span>
            </div>
          </div>
          <div className="card-body">
            {/* Inquiry Clicks Table */}
            <div className="table-responsive">
              {loading ? (
                <div className="text-center py-3">
                  <div
                    className="spinner-border spinner-border-sm text-primary"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2 text-muted">Loading...</span>
                </div>
              ) : (
                <table className="table" style={{borderCollapse: 'separate', borderSpacing: '0'}}>
                  <thead>
                    <tr>
                      <th>Sr.No</th>
                      <th>Manufacturing Company Name</th>
                      <th>Product Details</th>
                      <th>Tile Size</th>
                      <th>Grade</th>
                      <th>Buyer Information</th>
                      <th>Contact</th>
                      <th>Address</th>
                      <th>Last Clicked</th>
                      <th>Click Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiryClicks.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center py-4">
                          <div className="text-muted">
                            <i className="fas fa-search fa-2x mb-2"></i>
                            <p>No inquiry clicks found</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      inquiryClicks.map((click, index) => (
                        <tr 
                          key={click.buyer?.id || index}
                          style={{
                            backgroundColor: index % 2 === 0 ? '#ffffffff' : '#e0e0e0ff',
                            color: '#495057'
                          }}
                        >
                          <td   >
                            {(pagination.page - 1) * pagination.pageSize +
                              index +
                              1}
                          </td>
                          <td>
                            {click.product?.manufacturing_company_name || "N/A"}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <span className="me-2">{click.product?.tile_type_name}</span>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleViewProduct(click.product)}
                                title="View Product Details"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                            </div>
                          </td>
                          <td>{click.product?.tile_size_name}</td>
                          <td> {click.product?.tile_grade}</td>
                          <td>
                            <div>
                              <strong>
                                {click.buyer?.owner_name || "N/A"}
                              </strong>
                            </div>
                          </td>
                          <td>
                            <strong className="small">
                              {click.buyer?.office_mobile || "N/A"}
                            </strong>
                          </td>
                          <td>
                            <div>
                              <strong className="small">
                                {click.buyer?.address?.address_line || "N/A"}
                              </strong>
                              <div className="small">
                                {click.buyer?.address?.city},{" "}
                                {click.buyer?.address?.state_name_display}
                              </div>
                            </div>
                          </td>
                          <td>
                            <strong className="small">
                              {formatDate(click.last_clicked_at)}
                            </strong>
                          </td>
                          <td>
                            <span className="badge badge-info">
                              {click.click_count} click
                              {click.click_count !== 1 ? "s" : ""}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-3">
                <nav>
                  <ul className="pagination justify-content-center">
                    <li
                      className={`page-item ${
                        pagination.page === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {Array.from(
                      { length: pagination.totalPages },
                      (_, i) => i + 1
                    ).map((page) => (
                      <li
                        key={page}
                        className={`page-item ${
                          page === pagination.page ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    ))}

                    <li
                      className={`page-item ${
                        pagination.page === pagination.totalPages
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
                <div className="text-center mt-2">
                  <small className="text-muted">
                    Showing {(pagination.page - 1) * pagination.pageSize + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.pageSize,
                      pagination.total
                    )}{" "}
                    of {pagination.total} entries
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Details Modal */}
      {showModal && selectedProduct && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Product Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">Basic Information</h6>
                    <table className="table table-bordered">
                      <tbody>
                       <tr>
                          <td><strong>Manufacturing Company:</strong></td>
                          <td>{selectedProduct.manufacturing_company_name}</td>
                        </tr>
                        <tr>
                          <td><strong>Description:</strong></td>
                          <td>{selectedProduct.description}</td>
                        </tr>
                       
                        <tr>
                          <td><strong>Box Brand:</strong></td>
                          <td>{selectedProduct.box}</td>
                        </tr>
                        <tr>
                          <td><strong>Loading Address:</strong></td>
                          <td>{selectedProduct.loading_address}</td>
                        </tr>
                        <tr>
                          <td><strong>Remark:</strong></td>
                          <td>{selectedProduct.remark || "N/A"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">Tile Specifications</h6>
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <td width="40%"><strong>Tile Type:</strong></td>
                          <td width="60%">{selectedProduct.tile_type_name}</td>
                        </tr>
                        <tr>
                          <td><strong>Tile Size:</strong></td>
                          <td>{selectedProduct.tile_size_name}</td>
                        </tr>
                        <tr>
                          <td><strong>Tile Grade:</strong></td>
                          <td>
                            <span className="badge badge-success">
                              {selectedProduct.tile_grade}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Tile Body:</strong></td>
                          <td>{selectedProduct.tile_body}</td>
                        </tr>
                        <tr>
                          <td><strong>Thickness:</strong></td>
                          <td>{selectedProduct.thickness_mm} mm</td>
                        </tr>
                        <tr>
                          <td><strong>PCS per Box:</strong></td>
                          <td>{selectedProduct.pcs_per_box}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">Box Information</h6>
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <td width="40%"><strong>Box Weight:</strong></td>
                          <td width="60%">{selectedProduct.box_weight_kg} kg</td>
                        </tr>
                        <tr>
                          <td><strong>Working Sqft per Box:</strong></td>
                          <td>{selectedProduct.working_sqft_per_box}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">Status & Upload</h6>
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <td width="40%"><strong>Status:</strong></td>
                          <td width="60%">
                            <span className={`badge ${selectedProduct.is_active ? 'badge-success' : 'badge-danger'}`}>
                              {selectedProduct.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Published:</strong></td>
                          <td>
                            <span className={`badge ${selectedProduct.is_published ? 'badge-success' : 'badge-warning'}`}>
                              {selectedProduct.is_published ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>PDF Count:</strong></td>
                          <td>{selectedProduct.pdf_count}</td>
                        </tr>
                        <tr>
                          <td><strong>Uploaded At:</strong></td>
                          <td>{formatDate(selectedProduct.uploaded_at)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                {selectedProduct.hidden_district_name && selectedProduct.hidden_district_name.length > 0 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <h6 className="text-primary mb-3">Hidden Districts</h6>
                      <div className="d-flex flex-wrap">
                        {selectedProduct.hidden_district_name.map((district, index) => (
                          <span key={index} className="badge badge-warning me-2 mb-2">
                            {district}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default TileInquiryClickList;
